// --- React · 라우팅 ---
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 ---
import { css } from '@emotion/react';
import { useTheme } from '@emotion/react';
import type { AppTheme } from '@/theme/theme';
import { createAuthStyles } from '@/components/auth/authStyles';
import registerImage from '/images/register.png';

// --- 공통 UI 컴포넌트 ---
import { Input } from '@/components/ui/common/input/Input';
import { LanguageTabs } from '@/components/ui/common/LanguageTabs';
import { Button } from '@/components/ui';
import { ConfiguratorButton } from '@/components/auth';
import { GuideButton } from '@/features/guide';

// --- API ---
import { apiHelpers, extractApiData } from '@/api/core';
import { accessTokenExpiryISO } from '@/api/core/axios';

// --- 스토어 ---
import { useNotificationStore } from '../../store/notificationStore';
import { useAuthStore } from '../../store/authStore';
import { usePolicyStore } from '@/policy/policyStore';

// --- 타입 ---
import type {
  LoginRequest,
  LoginResponse,
  CompanyManagerContact,
  SiteNotAssignedError,
} from '@packages/types/auth/dto';

// --- i18n · 에러 파싱 ---
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { AUTH_ERRORS, parseApiError } from '@/utils/errors';

// --- 페이지 전용 모달 ---
import { SiteNotAssignedModal } from '@/components/modals/SiteNotAssignedModal';
import { InquiryModal } from '@/components/modals/InquiryModal';

const Login: React.FC = () => {
  const theme = useTheme() as AppTheme;
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotificationStore();
  const { login } = useAuthStore();
  const { setPolicyFromUser } = usePolicyStore();

  // ==================== 상태 관리 ====================

  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // 문의하기 모달
  const [showInquiryModal, setShowInquiryModal] = useState(false);

  // SiteManager에게 사이트 미배정 시 안내 모달
  const [showSiteNotAssignedModal, setShowSiteNotAssignedModal] = useState(false);
  const [companyManagerInfo, setCompanyManagerInfo] = useState<CompanyManagerContact | null>(null);

  // ==================== 핸들러 ====================

  /** 폼 필드 값 변경 (Register와 동일한 패턴) */
  const handleChange = (field: keyof LoginRequest) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /** 언어 변경 (Register와 동일) */
  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  /**
   * 로그인 폼 제출.
   * 흐름:
   *   1) API 요청 (rememberMe 옵션 포함)
   *   2) 토큰 저장 (sessionStorage)
   *   3) 유저 정보 스토어에 저장
   *   4) 성공 시 모니터링 페이지로 이동
   *   5) 실패 시 에러 처리 (SITE_NOT_ASSIGNED 특수 케이스 포함)
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      showError(AUTH_ERRORS.EMAIL_REQUIRED.title, AUTH_ERRORS.EMAIL_REQUIRED.message);
      return;
    }
    if (!formData.password) {
      showError(AUTH_ERRORS.PASSWORD_REQUIRED.title, AUTH_ERRORS.PASSWORD_REQUIRED.message);
      return;
    }

    setIsLoading(true);

    try {
      // useMutation 훅 대신 apiHelpers 직접 호출 — 로그인은 인증 전 단계라
      // React Query 캐싱이 불필요하고, 성공 시 토큰 저장 + 페이지 이동을 동기적으로 제어해야 함.
      const loginData = { ...formData, rememberMe };
      const response = await apiHelpers.post<LoginResponse>('auth.login', loginData);
      const result = extractApiData(response);

      if (!result?.access_token || !result.user) {
        showError(AUTH_ERRORS.UNKNOWN_ERROR.title, AUTH_ERRORS.UNKNOWN_ERROR.message);
        return;
      }

      // 2) 토큰 저장 (iOS 사생활 보호 모드 등 sessionStorage 예외 대비)
      try {
        sessionStorage.setItem('access_token', result.access_token);
        if (result.refresh_token) {
          sessionStorage.setItem('refresh_token', result.refresh_token);
        }
        // token_expiry는 access token의 실제 만료 시각이어야 한다.
        // 만료 후에는 axios 인터셉터가 refresh token으로 자동 갱신하므로,
        // 로그인 유지(7일)/일반(24시간) 세션은 refresh token 수명으로 보장된다.
        sessionStorage.setItem('token_expiry', accessTokenExpiryISO(result.expires_in));
      } catch {
        showError(AUTH_ERRORS.UNKNOWN_ERROR.title, AUTH_ERRORS.UNKNOWN_ERROR.message);
        return;
      }

      // 3) 유저 정보 스토어 저장
      login(result.user);
      setPolicyFromUser(result.user);
      window.dispatchEvent(new Event('bellcloud:fcm:sync'));

      // 4) 성공 → 모니터링 페이지로 이동
      showSuccess('auth:login_success', 'auth:welcome');
      navigate('/monitoring/realtime');
    } catch (error: unknown) {
      // 5) 에러 처리
      const axiosErr = error as {
        response?: { data?: Partial<SiteNotAssignedError> };
      };
      const errorData = axiosErr.response?.data;

      // SITE_NOT_ASSIGNED: 사이트 미배정 SiteManager 전용 에러 (구조화된 응답)
      if (errorData?.code === 'SITE_NOT_ASSIGNED') {
        setCompanyManagerInfo(errorData.companyManager ?? null);
        setShowSiteNotAssignedModal(true);
        return;
      }

      const e = parseApiError(error);
      showError(e.title, e.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== 렌더링 ====================

  const styles = createAuthStyles(registerImage, theme);

  return (
    <>
      {/* 사이트 미배정 안내 모달 */}
      <SiteNotAssignedModal
        isOpen={showSiteNotAssignedModal}
        onClose={() => setShowSiteNotAssignedModal(false)}
        companyManager={companyManagerInfo}
      />

      {/* 문의하기 모달 */}
      <InquiryModal
        isOpen={showInquiryModal}
        onClose={() => setShowInquiryModal(false)}
        source="login"
        prefill={{ email: formData.email }}
      />

      <div css={styles.container}>
        {/* 왼쪽 섹션 - 브랜드 이미지 (데스크톱 전용) */}
        <div css={styles.leftSection}>
          <div css={styles.leftOverlay} />
          <div css={styles.leftContent}>
            <div css={styles.leftText}>
              <img
                src="/images/bellcloud.svg"
                alt="BellCloud Logo"
                css={css`
                  width: 360px;
                  height: auto;
                  color: white;
                  filter: brightness(0) invert(1);
                `}
              />
            </div>
          </div>
        </div>

        {/* 오른쪽 섹션 - 로그인 폼 */}
        <div css={styles.rightSection}>
          <div css={styles.formContainer}>
            {/* 모바일 브랜드 바 */}
            <div css={styles.mobileBrandBar}>
              <img src="/images/bellcloud.svg" alt="BellCloud" css={styles.mobileLogo} />
            </div>

            {/* 타이틀 + 언어 선택 */}
            <div
              css={css`
                display: flex;
                gap: 1rem;
                flex-direction: column;
              `}
            >
              <div>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                  `}
                >
                  <h2 css={styles.formTitle}>{t(i18nKeys.auth.login)}</h2>
                  <GuideButton guideKey="login" iconOnly iconSize={24} />
                </div>
                <div css={styles.formTitleLine} />
              </div>

              <div
                css={css`
                  margin-left: auto;
                `}
              >
                <LanguageTabs
                  currentLanguage={i18n.language}
                  onLanguageChange={handleLanguageChange}
                />
              </div>
            </div>

            {/* 로그인 폼 */}
            <form css={styles.form} onSubmit={handleSubmit}>
              <div css={styles.fieldGroup}>
                {/* 이메일 필드 */}
                <div css={styles.field}>
                  <p>{t(i18nKeys.auth.e_mail)} *</p>
                  <Input
                    type="email"
                    name="email"
                    size="md"
                    value={formData.email}
                    onChange={handleChange('email')}
                    placeholder={t(i18nKeys.auth.e_mail)}
                    fullWidth
                  />
                </div>

                {/* 비밀번호 필드 */}
                <div css={styles.field}>
                  <p>{t(i18nKeys.auth.password)} *</p>
                  <Input
                    type="password"
                    name="password"
                    size="lg"
                    value={formData.password}
                    onChange={handleChange('password')}
                    placeholder={t(i18nKeys.auth.password)}
                    fullWidth
                  />
                </div>
              </div>

              {/* Remember Me 체크박스 */}
              <div css={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id="rememberMe"
                  css={styles.checkbox}
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                />
                <label htmlFor="rememberMe" css={styles.checkboxLabel}>
                  {t(i18nKeys.auth.remember_me)}
                </label>
              </div>

              {/* 로그인 버튼 */}
              <Button type="submit" variant="primary" disabled={isLoading} width="100%">
                {isLoading ? t(i18nKeys.auth.login_in_progress) : t(i18nKeys.auth.login)}
              </Button>
            </form>

            {/* 하단 링크 (회원가입, 비밀번호 찾기, 이메일 찾기) */}
            <div css={styles.bottomLinks}>
              <p css={styles.linkText}>
                {t(i18nKeys.auth.no_account_yet)}{' '}
                <Link to="/register" css={styles.link}>
                  {t(i18nKeys.auth.sign_up)}
                </Link>
              </p>
              <p css={styles.linkText}>
                {t(i18nKeys.auth.having_trouble_logging_in)}{' '}
                <Link to="/forgot-password" css={styles.link}>
                  {t(i18nKeys.auth.reset_password)}
                </Link>
                {' | '}
                <Link to="/find-email" css={styles.link}>
                  {t(i18nKeys.auth.find_email)}
                </Link>
                {' | '}
                <button
                  type="button"
                  css={[
                    styles.link,
                    css`
                      background: none;
                      border: none;
                      padding: 0;
                      cursor: pointer;
                      font-family: inherit;
                      font-size: inherit;
                    `,
                  ]}
                  onClick={() => setShowInquiryModal(true)}
                >
                  {t(i18nKeys.common.inquiry_menu)}
                </button>
              </p>
            </div>

            {/* 로그인 없이 기기 설정 (WebUSB/BLE 지원 브라우저만) */}
            <ConfiguratorButton />
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
